import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, KeyRound, DoorOpen, CalendarCheck, UtensilsCrossed, Music, Palette } from "lucide-react";
import { publicApi, CATEGORY_LABELS, CATEGORY_ORDER } from "@/lib/api";
import EveningCard from "@/components/EveningCard";
import DiscoveryMap from "@/components/DiscoveryMap";

const HERO = "/cover-community.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

function Hero() {
  return (
    <section className="relative h-screen min-h-[640px] flex items-end overflow-hidden grain">
      <img src={HERO} alt="A live performance in a château courtyard" className="absolute inset-0 w-full h-full object-cover img-duotone" />
      <div className="absolute inset-0 bg-gradient-to-t from-velvet via-velvet/65 to-velvet/45" />
      <div className="absolute inset-0 bg-wine-dark/30" />
      <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-8 lg:px-12 pb-20 lg:pb-28">
        <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.15 } } }}>
          <motion.div variants={fadeUp} className="eyebrow mb-5 flicker">Live cultural experiences across French heritage</motion.div>
          <motion.h1 variants={fadeUp} aria-label="Culture you can't generate" className="font-serif text-5xl sm:text-6xl lg:text-7xl text-parchment leading-[0.95] max-w-3xl">
            Culture you<br />can't generate.
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-6 text-lg text-parchment/80 max-w-xl font-sans">
            VIVANT is where you discover France's living culture — concerts, shows, festivals and gatherings inside its castles, estates and villages. Not a booking. An invitation into the real thing.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-5">
            <Link to="/#practices" data-testid="hero-browse-btn" className="btn-gold px-8 py-4 text-sm font-semibold tracking-wide flex items-center gap-2">
              Discover the evenings <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/#hotes" data-testid="hero-host-btn" className="text-parchment border border-parchment/40 px-8 py-4 text-sm tracking-wide hover:border-gold hover:text-gold transition-colors duration-300">
              Meet the hosts
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { icon: CalendarCheck, t: "Discover what's on", d: "Open the map and see the concerts, shows, festivals and tables happening around you — tonight, this weekend, or on your next trip." },
    { icon: KeyRound, t: "Reserve in a tap", d: "Grab your place in seconds. Many events are free, others just a few euros. Your ticket and the details land straight in the app." },
    { icon: DoorOpen, t: "Show up & live it", d: "Step into a floodlit courtyard, a village square, a candlelit hall. Real people, real places — the part of France no screen can give you." },
  ];
  return (
    <section id="how" className="relative bg-parchment py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div className="relative">
          <div className="overflow-hidden border border-stone-border">
            <img src="/upload-a.png" alt="An open-air performance at a heritage site" className="w-full h-full object-cover img-duotone aspect-[4/3]" />
          </div>
          <div className="absolute -bottom-5 -right-3 sm:right-6 bg-wine text-parchment px-5 py-3 font-display text-xs tracking-[0.25em]">
            LIVE · NOT STREAMED
          </div>
        </div>
        <div>
          <div className="eyebrow mb-3">How it works</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-wine max-w-xl leading-tight mb-10">
            From scrolling to actually being there, in three steps.
          </h2>
          <div className="space-y-8">
            {steps.map((s, i) => (
              <motion.div
                key={s.t}
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="flex gap-5"
              >
                <div className="flex flex-col items-center">
                  <span className="font-display text-gold text-lg">0{i + 1}</span>
                  {i < 2 && <span className="w-px flex-1 bg-stone-border mt-2" />}
                </div>
                <div className="pb-2">
                  <div className="flex items-center gap-3 mb-2">
                    <s.icon className="w-5 h-5 text-wine" />
                    <h3 className="font-serif text-2xl text-ink">{s.t}</h3>
                  </div>
                  <p className="text-ink-muted text-sm leading-relaxed max-w-md">{s.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Evenings() {
  const [cat, setCat] = useState("all");
  const { data: evenings = [], isLoading } = useQuery({
    queryKey: ["evenings", cat],
    queryFn: async () => (await publicApi.get(`/evenings${cat !== "all" ? `?category=${cat}` : ""}`)).data,
  });
  const tabs = [["all", "Everything"], ...CATEGORY_ORDER.map((c) => [c, CATEGORY_LABELS[c]])];
  return (
    <section id="evenings" className="bg-parchment-card py-24 lg:py-32 relative grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow mb-3">What's happening around you</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-wine">Dinners, concerts, theatre & more.</h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="category-tabs">
            {tabs.map(([key, label]) => (
              <button
                key={key}
                data-testid={`tab-${key}`}
                onClick={() => setCat(key)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-all duration-300 ${
                  cat === key ? "bg-wine text-parchment border-wine" : "border-stone-border text-ink-muted hover:border-gold"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-20 text-stone font-serif text-2xl">Lighting the candles…</div>
        ) : evenings.length === 0 ? (
          <div className="text-center py-20 text-stone">No evenings in this category yet.</div>
        ) : (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {evenings.map((e, i) => <EveningCard key={e.id} evening={e} index={i} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function MapTeaser() {
  const { data: evenings = [] } = useQuery({
    queryKey: ["evenings", "all"],
    queryFn: async () => (await publicApi.get(`/evenings`)).data,
  });
  return (
    <section id="map" className="relative bg-velvet text-parchment py-24 lg:py-32 overflow-hidden grain">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <div className="eyebrow mb-3">The secret geography</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-parchment leading-tight mb-6">
            Scattered across Paris<br />and the countryside.
          </h2>
          <p className="text-parchment/70 max-w-md leading-relaxed mb-5">
            Our houses hide in courtyards, behind gates, up unlit staircases and beyond the city in château country. Each glowing zone on the map marks the quarter of an upcoming evening — the precise door arrives only once your seat is confirmed.
          </p>
          <div className="flex items-center gap-6 text-sm text-parchment/60">
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "#C89B3C" }} /> Seats available</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "#8A8178" }} /> Complet</span>
          </div>
        </div>
        <DiscoveryMap evenings={evenings} />
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section id="manifesto" className="bg-wine text-parchment py-28 lg:py-36 relative grain">
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-8 text-center">
        <div className="eyebrow mb-8 text-gold-bright">Reconnecting people with people</div>
        <p className="font-serif text-3xl sm:text-4xl lg:text-[2.6rem] leading-[1.28] text-parchment">
          AI can generate images, music, videos and conversations. It cannot replace the feeling of a crowd at a concert, a performance in a 17th-century courtyard, or an unexpected evening shared with strangers. Technology should not replace human connection —
          <span className="text-gold-bright"> it should make it easier.</span>
        </p>
        <div className="hairline-gold w-40 mx-auto mt-12" />
        <p className="mt-8 font-display tracking-[0.35em] text-gold text-sm">DISCOVER · MEET · EXPERIENCE · LIVE</p>
      </div>
    </section>
  );
}

function WhyDifferent() {
  const points = [
    { n: "01", t: "Not another listings site", d: "France's real experiences are everywhere — but fragmented, hard to find, invisible to the people living nearby. We don't resell rooms or seats; we surface the living culture happening around you and give you a reason to go out tonight." },
    { n: "02", t: "A network, not a marketplace", d: "We don't build venues or produce every event. We connect the extraordinary cultural infrastructure France already has — castles, estates, villages, local authorities — into one living layer where partners share formats, artists and audiences." },
    { n: "03", t: "Every format, not just dinners", d: "A candlelit table, yes — but also a concert in a courtyard, open-air theatre, a village festival, a historical reconstruction, a conversation with an artist. One evening, or a whole weekend itinerary of real experiences." },
    { n: "04", t: "Heritage that pays for its future", d: "Venues keep their independence and their business model; we're the layer that fills their quiet nights and funds their upkeep. Good for guests, good for owners, good for the monuments themselves." },
  ];
  return (
    <section id="why" className="bg-velvet text-parchment py-24 lg:py-32 relative grain">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-16">
          <div className="eyebrow mb-3">Why VIVANT</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-parchment leading-tight">
            Not a booking app with a price tag. A way back into real life.
          </h2>
        </div>
        <div className="grid gap-px bg-parchment/10 border border-parchment/10 md:grid-cols-2">
          {points.map((p, i) => (
            <motion.div
              key={p.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="bg-velvet p-9 lg:p-11"
              data-testid={`why-point-${i}`}
            >
              <span className="font-display text-gold text-lg">{p.n}</span>
              <h3 className="font-serif text-2xl text-parchment mt-4 mb-3">{p.t}</h3>
              <p className="text-parchment/65 text-sm leading-relaxed">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HostCTA() {
  return (
    <section className="bg-parchment py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="relative overflow-hidden border border-stone-border">
          <img src="https://images.unsplash.com/photo-1734467551325-1b36ecbf9b8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover img-duotone" />
          <div className="absolute inset-0 bg-velvet/70" />
          <div className="relative z-10 p-10 sm:p-16 lg:p-24 max-w-2xl">
            <div className="eyebrow mb-4">For owners of houses with a history</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-parchment leading-tight mb-5">
              Your house has a story. Let it host an evening.
            </h2>
            <p className="text-parchment/75 mb-8 leading-relaxed">
              Château, hôtel particulier, atelier or farmhouse — if your walls have something to say, we bring you a table of curious guests and handle everything but the welcome. A new kind of evening, and a new kind of revenue.
            </p>
            <Link to="/host" data-testid="host-cta-btn" className="btn-gold inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold tracking-wide">
              Propose your house <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CulturalPractices() {
  const practices = [
    { icon: UtensilsCrossed, tag: "La Table", t: "The shared table", d: "The oldest cultural practice of all — strangers made intimate over slow courses and candlelight. A dinner, yes, but really a conversation that happens to be delicious." },
    { icon: Music, tag: "Le Salon", t: "Music & words", d: "The 19th-century Parisian salon, revived. Chamber music, readings and ideas heard up close in a room — not a hall — followed by supper with the artists themselves." },
    { icon: Palette, tag: "L'Atelier", t: "Made by hand", d: "Culture you make, not just watch. A craft, a recipe, a technique passed hand to hand by someone who has given their life to it — then enjoyed together." },
  ];
  return (
    <section id="practices" className="bg-parchment py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-16">
          <div className="eyebrow mb-3">A place to discover, not just reserve</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-wine leading-tight mb-4">
            Three living cultural practices, kept alive by the people who host them.
          </h2>
          <p className="text-ink-muted leading-relaxed">
            VIVANT isn't a listings site. It's a way into the France that happens behind closed doors — the table, the salon and the atelier, each an invitation to belong for an evening.
          </p>
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {practices.map((p, i) => (
            <motion.div
              key={p.tag}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className="card-parchment p-8"
              data-testid={`practice-${p.tag.toLowerCase().replace(/[^a-z]/g, "")}`}
            >
              <p.icon className="w-8 h-8 text-gold mb-5" />
              <div className="eyebrow mb-2">{p.tag}</div>
              <h3 className="font-serif text-2xl text-ink mb-3">{p.t}</h3>
              <p className="text-ink-muted text-sm leading-relaxed">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LesHotes() {
  const { data: hosts = [] } = useQuery({
    queryKey: ["hosts"],
    queryFn: async () => (await publicApi.get("/hosts")).data,
  });
  if (!hosts.length) return null;
  return (
    <section id="hotes" className="bg-parchment-card py-24 lg:py-32 relative grain">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-14">
          <div className="eyebrow mb-3">Les Hôtes</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-wine leading-tight mb-4">
            The people behind the doors.
          </h2>
          <p className="text-ink-muted leading-relaxed">
            Every evening belongs to a host — a countess, a chef, a cellist, a family. They are our true partners: the keepers of the houses and the practices you come to share.
          </p>
        </div>
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {hosts.slice(0, 6).map((h, i) => (
            <motion.div
              key={h.host_id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="flex gap-4 items-start"
              data-testid={`host-${h.host_id}`}
            >
              {h.avatar
                ? <img src={h.avatar} alt={h.name} className="w-16 h-16 object-cover img-duotone shrink-0" />
                : <div className="w-16 h-16 bg-wine/15 flex items-center justify-center font-serif text-2xl text-wine shrink-0">{h.name[0]}</div>}
              <div>
                <h3 className="font-serif text-xl text-ink leading-tight">{h.name}</h3>
                <p className="text-xs text-gold uppercase tracking-[0.15em] mt-1">{h.venue_type} · {h.city}</p>
                <p className="text-sm text-ink-muted mt-2 leading-relaxed line-clamp-3">{h.bio}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Partners() {
  const { data: partners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn: async () => (await publicApi.get("/partners")).data,
  });
  if (!partners.length) return null;
  return (
    <section id="partners" className="bg-parchment py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-14">
          <div className="eyebrow mb-3">Nos partenaires</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-wine leading-tight mb-4">
            A community of venues, not a catalogue of listings.
          </h2>
          <p className="text-ink-muted leading-relaxed">
            VIVANT is the connecting layer between the organisations keeping French heritage alive — foundations, tourism bodies, local authorities and guilds. Venues stay independent; together they share formats, artists and audiences, and make heritage more alive, attractive and sustainable.
          </p>
        </div>
        <div className="grid gap-px bg-stone-border border border-stone-border sm:grid-cols-2 lg:grid-cols-3" data-testid="partners-grid">
          {partners.map((p, i) => {
            const clickable = p.website && p.website !== "#";
            const Inner = (
              <>
                {p.logo && <img src={p.logo} alt={p.name} className="h-20 w-20 object-contain mb-5 opacity-90 group-hover:opacity-100 transition-opacity" />}
                <div className="font-display text-gold text-xs tracking-[0.25em] mb-2">{p.type.toUpperCase()}</div>
                <h3 className="font-serif text-2xl text-wine leading-tight mb-3 group-hover:text-wine-light transition-colors">{p.name}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{p.blurb}</p>
                {clickable && <span className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-gold">Visit <ArrowRight className="w-3.5 h-3.5" /></span>}
              </>
            );
            const cls = "bg-parchment-light p-8 group hover:bg-parchment-card transition-colors duration-300 block";
            return clickable ? (
              <a key={p.name} href={p.website} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`partner-${i}`}>{Inner}</a>
            ) : (
              <div key={p.name} className={cls} data-testid={`partner-${i}`}>{Inner}</div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Hero />
      <CulturalPractices />
      <WhyDifferent />
      <HowItWorks />
      <Evenings />
      <MapTeaser />
      <LesHotes />
      <Manifesto />
      <Partners />
      <HostCTA />
    </div>
  );
}
